import { CountrySelect } from "@/components/Country";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Layers, Play, RotateCcw, Mail } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CreditForecast } from "@/components/CreditForecast";
import { Textarea } from "@/components/ui/textarea";
import { TownSuggestions } from "@/components/TownSuggestions";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const CHIP = { done: "bg-[#2D6A4F]/10 text-[#2D6A4F]", failed: "bg-red-50 text-red-700", waiting: "bg-[#E2D9CC]/60 text-[#8C827A]" };

const BatchAction = ({ b, path, label, icon: Icon, done, testId, onDone }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/batches/${b.id}/${path}`); toast.success(done); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return <Button size="sm" data-testid={`${testId}-${b.id}`} onClick={go} disabled={busy} className="rounded-none h-8 ml-3"><Icon className="w-3.5 h-3.5 mr-1" />{busy ? "Working…" : label}</Button>;
};

const scheduled = (b) => b.guides.some((g) => g.status === "waiting") && new Date(b.run_after) > new Date();

const batchState = (b) => {
  const waiting = b.guides.filter((g) => g.status === "waiting").length;
  if (!waiting) return null;
  if (scheduled(b)) return `Waiting — starts ${new Date(b.run_after).toLocaleString("en-AU", { weekday: "short", hour: "numeric", minute: "2-digit" })}`;
  return b.guides.some((g) => !["waiting", "done", "failed"].includes(g.status)) ? `Building one at a time · ${waiting} waiting` : `${waiting} waiting to start`;
};

const BatchList = ({ batches, onChange }) => (
  <div className="space-y-6" data-testid="batch-list">
    {batches.length === 0 && <p data-testid="batch-empty" className="text-[#8C827A]">No batches yet.</p>}
    {batches.map((b) => {
      const done = b.guides.filter((g) => g.status === "done").length;
      const failed = b.guides.filter((g) => g.status === "failed").length;
      return (
        <div key={b.id} data-testid={`batch-${b.id}`} className="bg-[#F4EFE6] border border-[#E2D9CC] p-6">
          <div className="flex justify-between items-baseline mb-4">
            <p className="font-display text-xl text-[#3B1B48]">{b.guides.length} towns</p>
            <p className="text-xs text-[#8C827A]">{done}/{b.guides.length} done · starts {new Date(b.run_after).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</p>
          </div>
          {(batchState(b) || failed > 0) && (
            <div className="flex items-center flex-wrap gap-y-2 mb-4">
              {batchState(b) && <p data-testid={`batch-state-${b.id}`} className="text-sm text-[#B45309]">{batchState(b)}</p>}
              {scheduled(b) && <BatchAction b={b} path="start-now" label="Start now" icon={Play} done="Batch started" testId="batch-start-now" onDone={onChange} />}
              {failed > 0 && <BatchAction b={b} path="retry-failed" label={`Retry ${failed} failed`} icon={RotateCcw} done="Failed towns queued again" testId="batch-retry-failed" onDone={onChange} />}
            </div>
          )}
          {b.notified_at && <p data-testid={`batch-notified-${b.id}`} className="text-xs text-[#2D6A4F] mb-3 flex items-center gap-1"><Mail className="w-3.5 h-3.5" />Finished · summary emailed{b.notified_to ? ` to ${b.notified_to}` : ""}</p>}
          <div className="flex flex-wrap gap-2">
            {b.guides.map((g) => (
              <Link key={g.id} to={`/guides/${g.id}`} data-testid={`batch-guide-${g.id}`} title={g.error || ""}
                className={`px-3 py-1.5 text-xs transition-opacity hover:opacity-80 ${CHIP[g.status] || "bg-[#C59B27]/15 text-[#B45309]"}`}>
                <span className="font-display mr-1">{g.issue_number}</span>{g.town} · {g.status === "waiting" ? "waiting" : g.status === "done" || g.status === "failed" ? g.status : `${g.progress}%`}
              </Link>
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

export default function BatchPage() {
  const loc = useLocation();
  const [text, setText] = useState(loc.state?.towns?.join("\n") || "");
  const [start, setStart] = useState("now");
  const [startNumber, setStartNumber] = useState("");
  const [country, setCountry] = useState(loc.state?.country || "Italy");
  const [family, setFamily] = useState(false);
  const [batches, setBatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [credit, setCredit] = useState(null);
  const towns = text.split("\n").map((t) => t.trim()).filter(Boolean);

  const load = () => api.get("/batches").then((r) => setBatches(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const submit = async () => {
    const need = ((credit?.queued || 0) + towns.length) * (credit?.avg_cost || 0);
    if (credit?.balance != null && need > credit.balance &&
      !window.confirm(`These guides need about US$${need.toFixed(2)} of AI credit but your balance is about US$${credit.balance.toFixed(2)}. Only about ${credit.guides_left} will build before credit runs out. Queue anyway?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/batches", { towns, country, family, start, start_number: Number(startNumber) || null });
      toast.success(`${data.count} towns queued`);
      setText("");
      load();
      api.get("/credits").then((r) => setCredit(r.data)).catch(() => {});
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rise grid grid-cols-1 lg:grid-cols-[480px_1fr] gap-14">
      <div>
        <p className="eyebrow mb-3">Series production</p>
        <h1 className="font-display text-4xl sm:text-5xl text-[#3B1B48] mb-4">Batch builder</h1>
        <p className="text-[#8C827A] mb-8">One town per line; add the region after a comma if useful, and optionally an issue number at the end (e.g. "Todi, Umbria, 27"). Towns without a number get the next free numbers from the starting number. Guides build one after another (about 6 minutes each), and you get an email listing what built and what failed when the batch finishes.</p>
        <div className="space-y-2 mb-6">
          <Label className="text-[#3B1B48]">Country · series</Label>
          <CountrySelect testId="batch-country-select" value={country} onChange={setCountry} className="w-full" />
          <label className="flex items-center gap-2 text-sm text-[#3B1B48] pt-2 cursor-pointer" data-testid="batch-family-toggle">
            <input type="checkbox" checked={family} onChange={(e) => setFamily(e.target.checked)} className="accent-[#3B1B48] w-4 h-4" data-testid="batch-family-checkbox" />
            Families with children edition (its own "for Families" series and issue numbers)
          </label>
        </div>
        <Label htmlFor="towns" className="text-[#3B1B48]">Towns</Label>
        <Textarea id="towns" data-testid="batch-towns-input" rows={10} value={text} onChange={(e) => setText(e.target.value)}
          placeholder={"Todi, Umbria, 27\nMontefalco, Umbria\nMontepulciano, Tuscany"} className="mt-2 bg-white rounded-none font-mono-accent text-sm" />
        <div className="mt-6 space-y-2 max-w-[220px]">
          <Label htmlFor="batch-start-number" className="text-[#3B1B48]">Starting issue no. (optional)</Label>
          <Input id="batch-start-number" data-testid="batch-start-number-input" type="number" min="1" value={startNumber} onChange={(e) => setStartNumber(e.target.value)} placeholder="Next free" className="h-11 bg-white rounded-none" />
        </div>
        <div className="flex gap-2 mt-6" role="radiogroup">
          {[["now", "Start now"], ["tonight", "Build tonight (10pm Sydney)"]].map(([v, l]) => (
            <button key={v} data-testid={`batch-start-${v}`} onClick={() => setStart(v)}
              className={`px-4 py-2.5 text-sm border transition-colors ${start === v ? "bg-[#3B1B48] text-[#FAF7F2] border-[#3B1B48]" : "border-[#E2D9CC] text-[#3B1B48] hover:bg-[#EFE6F3]"}`}>{l}</button>
          ))}
        </div>
        <Button data-testid="batch-submit-button" onClick={submit} disabled={busy || towns.length === 0} className="mt-6 h-12 rounded-none px-6">
          <Layers className="w-4 h-4 mr-2" />{busy ? "Queuing…" : `Queue ${towns.length || ""} town${towns.length === 1 ? "" : "s"}`}
        </Button>
        {towns.length > 0 && <p data-testid="batch-estimate" className="text-xs text-[#8C827A] mt-3">Estimated time: about {towns.length * 6} minutes · about US${((credit?.avg_cost || 0) * towns.length).toFixed(2)} of AI credit.</p>}
        <CreditForecast newCount={towns.length} onCredit={setCredit} />
      </div>
      <div>
        <TownSuggestions country={country} onAdd={(lines) => setText((t) => [...t.split("\n").map((x) => x.trim()).filter(Boolean), ...lines.filter((l) => !t.includes(l))].join("\n"))} />
        <h2 className="font-display text-2xl text-[#3B1B48] mb-6">Batches</h2>
        <BatchList batches={batches} onChange={load} />
      </div>
    </div>
  );
}
